import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {ChartModule} from 'primeng/chart';
import {lastValueFrom} from 'rxjs';
import {DashboardService} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';
import {QueryKey} from '../../../../constants/query-key';
import {compactBarOptions, readCssVariable} from '../../charts/chart-theme';
import {formatEuros} from '../../dashboard.utils';

@Component({
  selector: 'elcia-dashboard-invoicing-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartModule, ElciaCard, Loader, TranslatePipe],
  templateUrl: './dashboard-invoicing-chart.html',
  host: {class: 'block min-w-0'},
})
export class DashboardInvoicingChart {
  private readonly dashboardService = inject(DashboardService);
  private readonly translate = inject(TranslateService);

  private readonly statsQuery = injectQuery(() => ({
    queryKey: [QueryKey.DASHBOARD_INVOICING_STATS],
    queryFn: () => lastValueFrom(this.dashboardService.getDashboardInvoicingStats()),
  }));

  protected readonly isLoading = computed(() => this.statsQuery.isPending());
  private readonly stats = computed(() => this.statsQuery.data()?.stats);

  protected readonly draftCount = computed(() => this.stats()?.draftCount ?? 0);
  protected readonly billedLabel = computed(() => formatEuros(this.stats()?.billedAmount ?? 0));

  protected readonly data = computed(() => ({
    labels: [
      this.translate.instant('dashboard.invoicing.to-bill'),
      this.translate.instant('dashboard.invoicing.billed'),
    ],
    datasets: [
      {
        data: [this.stats()?.draftAmount ?? 0, this.stats()?.billedAmount ?? 0],
        // Draft is the amount still sitting still, billed is the one that left: amber against emerald
        // reads as "pending" against "done" without needing a legend.
        backgroundColor: [
          readCssVariable('--p-amber-400', '#fbbf24'),
          readCssVariable('--p-emerald-500', '#10b981'),
        ],
        borderRadius: 6,
        maxBarThickness: 56,
      },
    ],
  }));

  protected readonly options = computed(() => compactBarOptions(formatEuros));
}
